import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { TrendingUp, TrendingDown, Globe, Zap, DollarSign, ArrowRight, PiggyBank, BarChart3, Loader2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';

function MetricCard({ label, value, subtext, icon: Icon, accent, large }) {
  return (
    <div className={cn("bg-card/60 border border-border rounded-xl p-4", large && "col-span-2")}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className={cn("font-bold mono", large ? "text-3xl" : "text-xl", accent)}>{value}</p>
      {subtext && <p className="text-[11px] text-muted-foreground mt-0.5">{subtext}</p>}
    </div>
  );
}

function CompareBar({ label, value, maxValue, color, pct }) {
  const width = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold mono">${value.toLocaleString()}{pct != null ? ` (${pct}%)` : ''}</span>
      </div>
      <div className="h-3 bg-muted rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-700", color)} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function MiniTrend({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.direct + d.ota), 1);
  return (
    <div className="flex items-end gap-1 h-12">
      {data.map((d, i) => {
        const directH = (d.direct / max) * 48;
        const otaH = (d.ota / max) * 48;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-px" title={d.month}>
            <div className="w-full flex flex-col gap-px">
              <div className="bg-emerald-500/80 rounded-t-sm" style={{ height: `${directH}px` }} />
              <div className="bg-amber-500/60 rounded-b-sm" style={{ height: `${otaH}px` }} />
            </div>
            <span className="text-[8px] text-muted-foreground">{d.month?.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function RevenueComparisonTab({ hotelId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/revenue-comparison`);
      const json = await res.json();
      setData(json);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hotelId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-muted-foreground text-center py-12">Failed to load revenue data.</p>;
  }

  const { summary, direct, ota, savings, ota_commission_rates, monthly_trend } = data;
  const hasOta = ota.bookings > 0;
  const hasDirect = direct.bookings > 0;

  return (
    <div className="space-y-6" data-testid="revenue-comparison-tab">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold heading-font flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          Revenue Comparison
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Direct bookings vs OTA bookings — see how much you save with getbitsy.ai
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total Revenue" value={`$${summary.total_revenue.toLocaleString()}`} icon={DollarSign} accent="text-foreground" />
        <MetricCard label="Total Bookings" value={summary.total_bookings} icon={BarChart3} />
        <MetricCard label="Direct Booking %" value={`${summary.direct_pct}%`} subtext={`${direct.bookings} direct / ${ota.bookings} OTA`} icon={Zap} accent="text-emerald-500" />
        <MetricCard label="Saved vs OTAs" value={`$${savings.saved_by_direct.toLocaleString()}`} subtext="By booking direct on getbitsy" icon={PiggyBank} accent="text-emerald-500" />
      </div>

      {/* Direct vs OTA Comparison */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Direct Bookings */}
        <Card data-testid="direct-revenue-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Direct Bookings</h3>
                  <p className="text-[10px] text-muted-foreground">via getbitsy.ai</p>
                </div>
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-500 text-xs">{direct.effective_fee_pct}% fee</Badge>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-muted-foreground">Gross Revenue</span>
                <span className="text-lg font-bold mono">${direct.revenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-baseline text-xs">
                <span className="text-muted-foreground">Platform Fee ({direct.effective_fee_pct}%)</span>
                <span className="mono text-amber-500">-${direct.platform_fees.toLocaleString()}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-medium">Net Revenue (You Keep)</span>
                <span className="text-lg font-bold mono text-emerald-500">${direct.net_revenue.toLocaleString()}</span>
              </div>

              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="text-[10px]">
                  {direct.crypto_bookings} crypto
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {direct.fiat_bookings} fiat
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {direct.bookings} total
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OTA Bookings */}
        <Card data-testid="ota-revenue-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">OTA Bookings</h3>
                  <p className="text-[10px] text-muted-foreground">via Channex (Booking.com, Expedia...)</p>
                </div>
              </div>
              <Badge className="bg-amber-500/10 text-amber-500 text-xs">{ota.avg_commission_pct}% avg</Badge>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-muted-foreground">Gross Revenue</span>
                <span className="text-lg font-bold mono">${ota.revenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-baseline text-xs">
                <span className="text-muted-foreground">OTA Commission ({ota.avg_commission_pct}%)</span>
                <span className="mono text-red-500">-${ota.commission_paid.toLocaleString()}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-medium">Net Revenue (You Keep)</span>
                <span className="text-lg font-bold mono text-amber-500">${ota.net_revenue.toLocaleString()}</span>
              </div>

              {hasOta && ota.breakdown?.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  {ota.breakdown.map((o) => (
                    <div key={o.name} className="flex justify-between text-[11px]">
                      <span className="text-muted-foreground">{o.name}</span>
                      <span className="mono">{o.bookings} bookings / ${o.revenue.toLocaleString()} ({o.commission_pct}%)</span>
                    </div>
                  ))}
                </div>
              )}

              {!hasOta && (
                <div className="text-center py-3">
                  <Globe className="w-6 h-6 mx-auto mb-1 opacity-20" />
                  <p className="text-xs text-muted-foreground">No OTA bookings yet. Connect Channex to see data here.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fee Comparison Visual */}
      <Card data-testid="fee-comparison-card">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm heading-font mb-4 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-emerald-500" />
            Commission Comparison
          </h3>
          <div className="space-y-3">
            <CompareBar
              label="getbitsy.ai (Direct)"
              value={savings.getbitsy_fee_pct}
              maxValue={25}
              color="bg-emerald-500"
              pct={null}
            />
            {Object.entries(ota_commission_rates).map(([name, pct]) => (
              <CompareBar
                key={name}
                label={name}
                value={pct}
                maxValue={25}
                color={name === 'Average OTA' ? 'bg-red-500' : 'bg-amber-500/60'}
                pct={null}
              />
            ))}
          </div>
          <div className="mt-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
            <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-start gap-2">
              <PiggyBank className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>You save {savings.fee_difference_pct}% per booking</strong> with getbitsy.ai vs the average OTA.
                {hasDirect && ` That's $${savings.saved_by_direct.toLocaleString()} saved so far on ${direct.bookings} direct bookings.`}
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      {monthly_trend?.length > 0 && (
        <Card data-testid="monthly-trend-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm heading-font mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Monthly Revenue Trend
            </h3>
            <div className="flex items-center gap-4 mb-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-emerald-500" /> Direct</span>
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-amber-500" /> OTA</span>
            </div>
            <MiniTrend data={monthly_trend} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
              {monthly_trend.map((m) => (
                <div key={m.month} className="bg-muted/30 rounded-lg p-2 text-[10px]">
                  <p className="font-medium text-xs">{m.month}</p>
                  <p className="text-emerald-500 mono">${m.direct.toLocaleString()} ({m.direct_count})</p>
                  <p className="text-amber-500 mono">${m.ota.toLocaleString()} ({m.ota_count})</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CTA */}
      {!hasOta && (
        <Card className="border-dashed">
          <CardContent className="p-5 text-center">
            <Info className="w-6 h-6 mx-auto mb-2 text-primary/50" />
            <p className="text-sm font-medium mb-1">Connect Channex to see full comparison</p>
            <p className="text-xs text-muted-foreground mb-3">
              Once OTA bookings flow in via Channex, you'll see a side-by-side comparison of direct vs OTA revenue with exact commission savings.
            </p>
            <Button variant="outline" size="sm" onClick={() => {
              // Navigate to channel manager tab
              const link = document.querySelector('[data-testid="owner-nav-channel-manager-link"]');
              if (link) link.click();
            }} data-testid="go-to-channel-manager-btn">
              <Globe className="w-4 h-4 mr-2" /> Go to Channel Manager
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
